import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { requestService, type Request } from '@/services/requestService'

interface RejectRequestModalProps {
  onSuccess?: () => void
  rejectRequest?: (requestId: string, reason: string) => Promise<void>
}

export interface RejectRequestModalRef {
  open: (request: Request) => void
  close: () => void
}

const RejectRequestModal = forwardRef<RejectRequestModalRef, RejectRequestModalProps>(({ onSuccess, rejectRequest }, ref) => {
  const { t } = useTranslation('requests')
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [request, setRequest] = useState<Request | null>(null)
  const [reason, setReason] = useState('')

  useImperativeHandle(ref, () => ({
    open: (requestData: Request) => {
      setRequest(requestData)
      setIsOpen(true)
      setError('')
      setSuccess(false)
      setReason('')
    },
    close: () => {
      setIsOpen(false)
      setRequest(null)
      setReason('')
      setSuccess(false)
    },
  }))

  const handleReject = async () => {
    if (!request) return

    try {
      setLoading(true)
      setError('')

      // Use custom rejectRequest function if provided, otherwise use default requestService
      if (rejectRequest) {
        await rejectRequest(request.id, reason)
      } else {
        await requestService.rejectRequest(request.id, reason)
      }

      // Show success message instead of closing immediately
      setSuccess(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    const wasSuccess = success
    setIsOpen(false)
    setRequest(null)
    setReason('')
    setError('')
    setSuccess(false)

    // Only call onSuccess if the operation was successful
    if (wasSuccess) {
      onSuccess?.()
    }
  }

  if (!isOpen || !request) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/30 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-red-600">{t('rejectModal.title')}</h2>
              <p className="text-sm text-gray-600 mt-1">{t('rejectModal.subtitle')}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Success Message */}
          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900 mb-1">
                    {t('rejectModal.successTitle')}
                  </p>
                  <p className="text-xs text-green-700">
                    {t('rejectModal.successDesc')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && !success && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Request Info */}
          {!success && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-3">{t('rejectModal.message')}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{t('table.type')}:</span>
                  <span className="text-sm text-gray-900">{t(`requestTypes.${request.type}`)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{t('table.requestor')}:</span>
                  <span className="text-sm text-gray-900">{request.requestor.name || request.requestor.account}</span>
                </div>
              </div>
            </div>
          )}

          {/* Reason Input */}
          {!success && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('rejectModal.reason')} <span className="text-gray-500">({t('rejectModal.optional')})</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('rejectModal.reasonPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                rows={3}
                disabled={loading}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          {success ? (
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('rejectModal.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                {t('rejectModal.cancel')}
              </button>
              <button
                type="button"
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={loading}
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{loading ? t('rejectModal.rejecting') : t('rejectModal.reject')}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
})

RejectRequestModal.displayName = 'RejectRequestModal'

export default RejectRequestModal
